<template>
  <b-modal @shown="modalShown" id="branching-setup-modal" ref="branchingSetupModal" :hide-header-close="noCloseOnBackdrop" hide-footer :no-close-on-esc="noCloseOnBackdrop" :no-close-on-backdrop="noCloseOnBackdrop" :modal-class="modalWidthClass">
    <template slot="modal-title">Step {{ selectedQuestionForBranching.stepSequence }} Branch Logic</template>
    <div class="question-title">{{ selectedQuestionForBranching.body }}</div>
    <div v-if="selectedQuestion">
      <div v-if="selectedQuestion.type == 'MultiChoice' && selectedQuestion.multipleSelection == true" class="multiple-choice-ms">
        <div class="recursive-branching-toggle">
          Recursive branching
          <span
            v-b-popover.hover="
              `Recursive branching allows you to configure branching logic to each available response within a multiple choice question. If multiple selections are made, the participant will be taken to each branch automatically in subsequent order, creating a recursive loop. Steps that already have defined branching logic are not available for selection within the &quot;Loop through&quot; dropdown.`
            "
            class="fa fa-info-circle"
          ></span>

          <label class="switch-toggle-btn">
            <input type="checkbox" v-model="selectedQuestion.recursiveBranching" value="true" />
            <div class="slider"></div>
            <div class="text"></div>
          </label>
        </div>
        <div class="advance-branching">
          <b-row class="header" :no-gutters="true">
            <b-col cols="8" class="my-auto">If the answer is:</b-col>
            <b-col cols="4" class="my-auto pl-21">{{ selectedQuestion.recursiveBranching ? "Loop through" : "Skip to" }}:</b-col>
          </b-row>
          <div v-if="selectedQuestion.recursiveBranching" class="recursive-branching model-fixed-branching">
            <b-row v-for="(option, index) in selectedQuestion.options" :id="`branching-row-${option.id}-${index}`" :key="`branching-row-${option.id}-${index}`" :no-gutters="true" class="branching-opt-row" :class="{ 'has-error': option.error.length }">
              <b-col cols="8" class="my-auto" :class="{ 'mb-10': option.error.length }">
                <div class="d-inline-block mr-8 w-173">
                  {{ option.body }}
                </div>
              </b-col>
              <b-col cols="4" class="my-auto text-right" :class="{ 'mb-10': option.error.length }">
                <div class="d-inline-block mr-8 w-173">
                  <LoopThroughDropDown :options="activityQuestions" :error="option.isSkipToStepEmpty" :questionTypes="questionTypes" :filterSteps="option.filterSteps" :selectedLoopThroughIds="option.nextQuestionIdList" @input="optionInputHandler($event, index)" />
                </div>
                <div class="d-inline-block my-auto text-right">
                  <img alt="delete" src="static/img/light-gray-delete.svg" @click="resetSelection(index)" class="trash-can" :class="{ disabled: option.disableTrashCan }" />
                </div>
              </b-col>
              <div v-for="(error, errIndex) in option.error" :id="`branching-error-${errIndex}`" :key="`branching-error-${errIndex}`" class="error-text">
                {{ error }}
              </div>
            </b-row>
          </div>
          <div v-else class="non-recursive-branching model-fixed-branching">
            <b-row v-for="(branchingOption, index) in selectedQuestion.branchingOptions" :id="`branching-row-${index}`" :key="`branching-row-${index}`" :no-gutters="true" class="branching-opt-row" :class="{ 'has-error': branchingOption.error.length }">
              <b-col
                cols="8"
                class="my-auto"
                :class="{
                  'mb-10': branchingOption.error.length || branchingOption.optionIds.length
                }"
              >
                <div class="d-inline-block mr-8 w-173">
                  <MultiChoiceOptionsDropDown :options="selectedQuestion.options" :error="branchingOption.optionHasError" :selectedOptionId="branchingOption.optionIds" @input="optionInputHandler($event, index)" />
                </div>
              </b-col>
              <b-col
                cols="4"
                class="my-auto text-right"
                :class="{
                  'mb-10': branchingOption.error.length || branchingOption.optionIds.length
                }"
              >
                <div class="d-inline-block mr-8 w-173">
                  <SkipToDropDown :options="activityQuestions" :error="branchingOption.isSkipToStepEmpty" :questionTypes="questionTypes" :selectedOptionId="branchingOption.nextQuestionId" @input="InputHandler($event, index)" />
                </div>
                <div class="d-inline-block my-auto text-right">
                  <img alt="delete" src="static/img/light-gray-delete.svg" @click="resetSelection(index)" class="trash-can" :class="{ disabled: branchingOption.disableTrashCan }" />
                </div>
              </b-col>

              <div v-if="branchingOption.optionIds.length" class="selected-options-chips">
                <template v-if="optIndex < branchingOption.chipsLimitBy">
                  <span v-for="(opt, optIndex) in branchingOption.optionIds" :id="`branching-opt-${opt.id}-${optIndex}`" :key="`branching-opt-${opt.id}-${optIndex}`" class="chip">
                    {{ opt.body }}
                    <span @click="$delete(branchingOption.optionIds, optIndex)" class="fa fa-times-circle"></span>
                  </span>
                </template>
                <div>
                  <a href="javascript:void(0)" v-if="branchingOption.optionIds.length > 6" class="see-more-less" @click="toggleChips(branchingOption)">{{ branchingOption.chipsLimitBy === 6 ? "See more" : "See less" }}</a>
                </div>
              </div>

              <div v-for="(error, errIndex) in branchingOption.error" :id="`branching-error-${errIndex}`" :key="`branching-error-${errIndex}`" class="error-text">
                {{ error }}
              </div>
            </b-row>
          </div>
        </div>
      </div>
      <div v-else-if="generalBranching" class="general-branching">
        <b-row :no-gutters="true">
          <b-col cols="7" class="my-auto">Skip to:</b-col>
          <b-col cols="4">
            <SkipToDropDown :options="activityQuestions" :questionTypes="questionTypes" :selectedOptionId="selectedQuestion.nextQuestionId" @input="InputHandler($event)" />
          </b-col>
          <b-col cols="1" class="my-auto text-right"><img alt="delete" src="static/img/light-gray-delete.svg" @click="resetSelection()" class="trash-can" :class="{ disabled: disableTrashCan }"/></b-col>
        </b-row>
      </div>
      <div v-else>
        <div class="form-group">
          <div class="form-check form-check-inline" v-for="(option, index) in branchingTypeOptions" :key="`branching-type-option-${option.value}-${index}`">
            <input v-model="selectedQuestion.branchingType" class="form-check-input" type="radio" v-bind:value="option.value" :id="`branching-type-${index}-${option.value}`" />
            <label class="form-check-label" :for="`branching-type-${index}-${option.value}`">{{ option.text }}</label>
          </div>
        </div>
        <div class="normal-branching" :class="[selectedQuestion.branchingType == normal ? '' : 'disabled']">
          <b-row :no-gutters="true">
            <b-col cols="6">
              <div class="my-auto d-inline-block mr-10">Skip to:</div>
              <div class="d-inline-block mr-10 w-173">
                <SkipToDropDown :options="activityQuestions" :questionTypes="questionTypes" :selectedOptionId="selectedQuestion.nextQuestionId" @input="InputHandler($event)" />
              </div>
              <div class="d-inline-block my-auto text-right">
                <img alt="delete" src="static/img/light-gray-delete.svg" @click="resetSelection()" class="trash-can" :class="{ disabled: disableTrashCan }" />
              </div>
            </b-col>
          </b-row>
        </div>
        <div class="advance-branching" :class="[selectedQuestion.branchingType == normal ? 'disabled' : '']">
          <b-row class="header" :no-gutters="true">
            <b-col cols="8" class="my-auto">If the answer is:</b-col>
            <b-col cols="4" class="my-auto pl-21">Skip to:</b-col>
          </b-row>
          <div class="model-fixed-branching">
            <b-row v-for="(branchingOption, index) in selectedQuestion.branchingOptions" :id="`branching-row-${index}`" :key="`branching-row-${index}`" :no-gutters="true" class="branching-opt-row" :class="{ 'has-error': branchingOption.error.length }">
              <b-col
                cols="8"
                class="my-auto"
                :class="{
                  'no-empty-error': branchingOption.noEmptyRange && branchingOption.error.length == 1,
                  'mb-10': branchingOption.error.length
                }"
              >
                <input type="number" v-model="branchingOption.startValue" class="form-control d-inline-block w-97" />
                <span class="mr-8 ml-8">to</span>
                <input type="number" v-model="branchingOption.endValue" class="form-control d-inline-block w-97" />
              </b-col>
              <b-col cols="4" class="my-auto text-right" :class="{ 'mb-10': branchingOption.error.length }">
                <div class="d-inline-block mr-8 w-173">
                  <SkipToDropDown :options="activityQuestions" :error="branchingOption.isSkipToStepEmpty" :questionTypes="questionTypes" :selectedOptionId="branchingOption.nextQuestionId" @input="InputHandler($event, index)" />
                </div>
                <div class="d-inline-block my-auto text-right">
                  <img alt="delete" src="static/img/light-gray-delete.svg" @click="resetSelection(index)" class="trash-can" :class="{ disabled: branchingOption.disableTrashCan }" />
                </div>
              </b-col>
              <div v-for="(error, errIndex) in branchingOption.error" :id="`branching-error-${errIndex}`" :key="`branching-error-${errIndex}`" class="error-text">
                {{ error }}
              </div>
              <div v-if="selectedQuestion.hasMinMax" class="min-max">
                <span class="min">Minimum: {{ selectedQuestion.minValue }}</span>
                <span class="min"> | Maximum: {{ selectedQuestion.maxValue }}</span>
              </div>
            </b-row>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <div class="footer">
          <b-row :no-gutters="true" :class="{ 'adjust-col-width': !generalBranching }">
            <b-col cols="8" class="my-auto">
              <div v-if="selectedQuestion.type == 'MultiChoice' && selectedQuestion.multipleSelection == true && selectedQuestion.recursiveBranching">
                Once recursive loop is complete, skip to
                <div class="d-inline-block ml-18 w-173">
                  <SkipToDropDown :options="recursiveLoopEndQuestions" :alignBottom="true" :error="selectedQuestion.recursiveLoopEndQuestionEmpty" :questionTypes="questionTypes" :selectedOptionId="selectedQuestion.recursiveLoopEndQuestionId" @input="InputHandler($event)" />
                </div>
                <div class="d-inline-block my-auto ml-8">
                  <img alt="delete" src="static/img/light-gray-delete.svg" @click="resetSelection()" class="trash-can" :class="{ disabled: disableTrashCan }" />
                </div>
              </div>
              <b-button v-else-if="!generalBranching" class="btn-add-another" :class="[selectedQuestion.branchingType == normal ? 'disabled' : '']" @click="addAnotherBranchingRow()">
                Add another branch
              </b-button>
            </b-col>
            <b-col cols="4" class="my-auto text-right">
              <b-button class="btn-cancel" @click="hide()">
                Cancel
              </b-button>
              <b-button :disabled="disableSaveButton" variant="primary" @click="saveBranching()">
                Save
              </b-button>
            </b-col>
            <div v-for="(error, errIndex) in selectedQuestion.error" :id="`branching-error-${errIndex}`" :key="`branching-error-${errIndex}`" class="error-text">
              {{ error }}
            </div>
          </b-row>
        </div>
      </template>
    </div>
  </b-modal>
</template>

<script>
import SkipToDropDown from "@/components/activity-library/skip-to-dropdown";
import MultiChoiceOptionsDropDown from "@/components/activity-library/multichoice-options-dropdown";
import LoopThroughDropDown from "@/components/activity-library/loop-through-dropdown";
import { BranchingHelper } from "@/components/activity-library/branching-helper";
import eventBus from "@/eventBus";

import _ from "lodash";
export default {
  name: "branching-setup-modal-component",
  props: {
    selectedQuestionForBranching: {
      type: Object,
      required: true
    },
    activityQuestions: {
      type: Array,
      required: true
    },
    questionTypes: {
      type: Object,
      required: true
    }
  },
  components: {
    SkipToDropDown,
    MultiChoiceOptionsDropDown,
    LoopThroughDropDown
  },
  data() {
    const NORMAL = "normal";
    const ADVANCE = "advance";
    return {
      BranchingHelper: new BranchingHelper(),
      normal: NORMAL,
      advance: ADVANCE,
      noCloseOnBackdrop: true,
      generalBranchingQuestion: ["DateTime", "Text"],
      branchingTypeOptions: [
        {
          value: NORMAL,
          text: "Any answer"
        },
        {
          value: ADVANCE,
          text: "Advanced branching"
        }
      ],
      defaultBranchingOptions: {
        startValue: "",
        endValue: "",
        nextQuestionId: "",
        error: []
      },
      multipleChoiceBranchingOptions: {
        optionIds: [],
        nextQuestionId: "",
        error: [],
        chipsLimitBy: 6
      },
      selectedQuestion: null,
      default_chips_limit: 6,
      recursiveLoopEndQuestions: []
    };
  },
  watch: {
    activityQuestions: {
      handler() {},
      deep: true
    },
    selectedQuestionForBranching: {
      deep: true,
      handler(val) {
        if (!val.id) {
          return;
        }
        this.selectedQuestion = _.cloneDeep(val);
        if (!this.selectedQuestion.branchingType) {
          this.selectedQuestion.branchingType = this.normal;
        }
      }
    },
    selectedQuestion: {
      deep: true,
      handler(selectedQuestion) {
        let disableTrashCan = false;
        //--------------Toggle trash can-------------------
        if (selectedQuestion.branchingOptions && selectedQuestion.branchingOptions.length) {
          const branchingOptions = selectedQuestion.branchingOptions;
          const branchingOptionsLength = branchingOptions.length;

          for (let a = 0; a < branchingOptionsLength; a++) {
            disableTrashCan = false;
            if (a == 0) {
              //As per the req. we have to toggle first row only.
              disableTrashCan = this.BranchingHelper.disableTrashCan(branchingOptions[a]);
            }
            //Rest rows will remain disabled
            if (branchingOptions[a].hasOwnProperty("disableTrashCan")) {
              branchingOptions[a].disableTrashCan = disableTrashCan;
            } else {
              this.$set(branchingOptions[a], "disableTrashCan", disableTrashCan);
            }
          }
        }

        if (selectedQuestion.options && selectedQuestion.options.length) {
          const options = selectedQuestion.options;
          const optionsLength = options.length;
          for (let a = 0; a < optionsLength; a++) {
            disableTrashCan = this.BranchingHelper.disableTrashCan(options[a]);
            if (options[a].hasOwnProperty("disableTrashCan")) {
              options[a].disableTrashCan = disableTrashCan;
            } else {
              this.$set(options[a], "disableTrashCan", disableTrashCan);
            }
          }
        }
      }
    },
    "selectedQuestion.branchingType": {
      handler() {}
    },
    "selectedQuestion.recursiveBranching": {
      handler() {
        this.resetErrors();
      }
    },
    recursiveLoopEndQuestions: {
      deep: true,
      handler(loopEndQuestions) {
        //Reset LoopEndQuestion
        const loopEndQues = _.find(loopEndQuestions, {
          id: this.selectedQuestion.recursiveLoopEndQuestionId
        });
        if (loopEndQues == undefined) {
          this.selectedQuestion.recursiveLoopEndQuestionId = null;
        }
      }
    }
  },
  computed: {
    disableSaveButton() {
      if (this.selectedQuestion.branchingConfigured) {
        //Branching exists keep save button enabled
        return false;
      }

      if (this.selectedQuestion.branchingType == this.normal && this.BranchingHelper.checkIfNextQuestionHasValue(this.selectedQuestion.nextQuestionId)) {
        return false;
      }

      if (!this.generalBranching && this.selectedQuestion.branchingType == this.advance && this.selectedQuestion.type != "MultiChoice") {
        //At first entry of data in the first branching row (whether 'Any answer' or 'Advanced branching'), the system will automatically enable the Save button.
        if (this.selectedQuestion.branchingOptions[0].startValue != "" || this.selectedQuestion.branchingOptions[0].endValue != "" || this.BranchingHelper.checkIfNextQuestionHasValue(this.selectedQuestion.branchingOptions[0].nextQuestionId)) {
          return false;
        }
      }

      if (!this.generalBranching && this.selectedQuestion.branchingType == this.advance && this.selectedQuestion.type == "MultiChoice") {
        if (this.selectedQuestion.recursiveBranching && this.selectedQuestion.recursiveBranching == true) {
          return false;
        }

        //At first entry of data in the first branching row, the system will automatically enable the Save button.
        if (this.selectedQuestion.branchingOptions[0].optionIds) {
          if (this.selectedQuestion.branchingOptions[0].optionIds.length > 0 || this.BranchingHelper.checkIfNextQuestionHasValue(this.selectedQuestion.branchingOptions[0].nextQuestionId)) {
            return false;
          }
        }
      }

      if (!this.generalBranching && this.selectedQuestion.branchingType == this.advance && this.selectedQuestion.branchingOptions.length > 1) {
        //Add another branching row
        //Selecting 'Add another branch' will automatically enable the Save button.
        return false;
      }

      if (!this.selectedQuestion.branchingConfigured) {
        //No branching keep save button disabled
        return true;
      }
      return false;
    },
    modalWidthClass() {
      return this.generalBranching ? "general" : "non-general";
    },
    generalBranching() {
      if (!this.selectedQuestion) {
        return true;
      }
      return !!this.generalBranchingQuestion.includes(this.selectedQuestion.type);
    },
    disableTrashCan() {
      if (this.selectedQuestion.type == "MultiChoice" && this.selectedQuestion.recursiveBranching) {
        return !this.selectedQuestion.recursiveLoopEndQuestionId;
      } else {
        return !this.BranchingHelper.checkIfNextQuestionHasValue(this.selectedQuestion.nextQuestionId);
      }
    }
  },
  beforeDestroy() {},
  methods: {
    modalShown() {
      // Function to attach event listeners once the modal is shown
      const modalElement = document.getElementById("branching-setup-modal");
      if (modalElement) {
        // Array of events to listen for
        const eventsToHandle = ["click", "keypress", "keydown", "keyup", "scroll", "dblclick", "change", "select", "focusout"];
        // Attaching event listeners for each event type
        eventsToHandle.forEach(event => {
          modalElement.addEventListener(event, (event) => {
            eventBus.$emit("CheckModalIdleTime", event);
          });
        });
      } 
    },
    show() {
      this.$refs.branchingSetupModal.show();
    },
    hide() {
      this.$refs.branchingSetupModal.hide();
    },
    optionInputHandler(e, index) {
      if (!e) {
        return;
      }

      eventBus.$emit("CheckModalIdleTime", e);
      if (this.selectedQuestion.recursiveBranching) {
        this.selectedQuestion.options[index].nextQuestionIdList = e;
        //Prepare RecursiveLoop End Question
        this.loopEndQuestions();
        //Remove step from subsequent dropdowns
        this.filterStepsForSubsequentDropDown();
      } else {
        this.selectedQuestion.branchingOptions[index].optionIds = _.sortBy(e, ["sequence"]);
      }
    },
    InputHandler(e, index) {
      if (!e) {
        return;
      }
      
      eventBus.$emit("CheckModalIdleTime", e);
      if ((index != undefined || index != null) && index >= 0) {
        this.selectedQuestion.branchingOptions[index].nextQuestionId = e && e.id ? e.id : "";
      } else if (this.selectedQuestion.type == "MultiChoice" && this.selectedQuestion.recursiveBranching) {
        this.selectedQuestion.recursiveLoopEndQuestionId = e.id;
      } else {
        this.selectedQuestion.nextQuestionId = e.id;
      }
    },
    filterStepsForSubsequentDropDown() {
      for (let i = 0; i < this.selectedQuestion.options.length; i++) {
        this.selectedQuestion.options[i].filterSteps = this.getFilterSteps(i);
      }
    },
    getFilterSteps(index) {
      let filteredSteps = [];
      for (let i = 0; i < this.selectedQuestion.options.length; i++) {
        if (i != index) {
          filteredSteps = _.concat(filteredSteps, this.selectedQuestion.options[i].nextQuestionIdList);
        }
      }
      return filteredSteps;
    },
    resetSelection(resetIndex = null) {
      if (resetIndex == null) {
        //Its a General Branching
        this.selectedQuestion.nextQuestionId = null;

        if (this.selectedQuestion.recursiveLoopEndQuestionId) {
          this.selectedQuestion.recursiveLoopEndQuestionId = null;
        }
      } else {
        if (this.selectedQuestion.type == "MultiChoice" && this.selectedQuestion.multipleSelection == true && this.selectedQuestion.recursiveBranching) {
          this.selectedQuestion.options[resetIndex].nextQuestionIdList = [];

          //Prepare RecursiveLoop End Question
          this.loopEndQuestions();
          //Remove step from subsequent dropdowns
          this.filterStepsForSubsequentDropDown();
        }
        //Questions where we have normal & advance branching both
        else if (this.selectedQuestion.branchingOptions.length == 1) {
          this.setBranchingOptions(false);
        } else {
          this.$delete(this.selectedQuestion.branchingOptions, resetIndex);
        }
      }
    },
    setBranchingOptions(push = true) {
      if (this.selectedQuestion.type == "MultiChoice" && this.selectedQuestion.multipleSelection == true) {
        //Non Recursive MC MS
        if (push) {
          this.selectedQuestion.branchingOptions.push({
            ...this.multipleChoiceBranchingOptions
          });
        } else {
          this.selectedQuestion.branchingOptions = [
            {
              ...this.multipleChoiceBranchingOptions
            }
          ];
        }
      } else {
        if (push) {
          this.selectedQuestion.branchingOptions.push({
            ...this.defaultBranchingOptions
          });
        } else {
          this.selectedQuestion.branchingOptions = [
            {
              ...this.defaultBranchingOptions
            }
          ];
        }
      }
    },
    saveBranching() {
      if (!this.generalBranching) {
        this.validateBranchingOptions();
      }

      if (this.branchingHasAnyErrors()) {
        return;
      }

      this.resetBranchingConfig();

      this.$emit("saveBranching", this.selectedQuestion);
      this.hide();
    },
    addAnotherBranchingRow() {
      this.setBranchingOptions();
    },
    resetBranchingConfig() {
      //Check for all empty values in a branching row if so then remove that row
      this.deleteEmptyBranchingRow();

      if (this.selectedQuestion.type == "MultiChoice" && this.selectedQuestion.multipleSelection == true) {
        if (this.selectedQuestion.recursiveBranching) {
          //Reset branching options
          this.selectedQuestion.branchingOptions = [];
        } else {
          this.selectedQuestion.recursiveLoopEndQuestionId = null;
          //Reset nextQuestionIdList from options
          for (let i = 0; i < this.selectedQuestion.options.length; i++) {
            this.selectedQuestion.options[i].nextQuestionIdList = [];
          }
        }
      } else if (this.selectedQuestion.branchingOptions && this.selectedQuestion.branchingType == this.normal) {
        this.selectedQuestion.branchingOptions = [];
      } else if (!this.generalBranching && this.selectedQuestion.branchingType == this.advance) {
        this.selectedQuestion.nextQuestionId = null;
      }
    },
    validateBranchingOptions() {
      this.resetErrors();

      if (this.selectedQuestion.type == "MultiChoice" && this.selectedQuestion.multipleSelection == true) {
        this.validateMultiChoiceMuliSelect();
      } else if (this.selectedQuestion.branchingType == this.advance) {
        this.validateRangeOptions();
      }
    },
    validateMultiChoiceMuliSelect() {
      if (this.selectedQuestion.recursiveBranching) {
        if (this.selectedQuestion.recursiveLoopEndQuestionId == null && this.hasAnyLoopThroughStepSelected()) {
          //It means any loop thorugh step is selected but once recursive loop end question is not
          this.$set(this.selectedQuestion, "recursiveLoopEndQuestionEmpty", true);
          this.$set(this.selectedQuestion, "error", []);
          this.selectedQuestion.error.push("Please populate all required fields.");
        } else if (this.selectedQuestion.recursiveLoopEndQuestionId != null && !this.hasAnyLoopThroughStepSelected()) {
          this.$set(this.selectedQuestion, "error", []);
          this.selectedQuestion.error.push("Please populate all required fields.");
        }
      } else {
        const branchingOptions = this.selectedQuestion.branchingOptions;
        const branchingOptionsLength = this.selectedQuestion.branchingOptions.length;
        for (let a = 0; a < branchingOptionsLength; a++) {
          if (branchingOptions[a].optionIds.length > 0 && this.BranchingHelper.checkIfNextQuestionHasValue(branchingOptions[a].nextQuestionId)) {
            //All Filled
          } else if (branchingOptions[a].optionIds.length == 0 && this.BranchingHelper.checkIfNextQuestionIsEmpty(branchingOptions[a].nextQuestionId)) {
            //All Empty
          } else {
            if (branchingOptions[a].optionIds.length == 0) {
              //Options are not selected.
              this.$set(branchingOptions[a], "optionHasError", true);
            }

            if (this.BranchingHelper.checkIfNextQuestionIsEmpty(branchingOptions[a].nextQuestionId)) {
              //Skip to step is not selected.
              this.$set(branchingOptions[a], "isSkipToStepEmpty", true);
            }

            this.selectedQuestion.branchingOptions[a].error.push("Please populate all required fields.");
          }
        } //End For Loop

        //Validations for unique set of options
        let i, x;
        for (i = 0; i < branchingOptionsLength - 1; i++) {
          const optionIds1 = branchingOptions[i].optionIds;
          for (x = i + 1; x < branchingOptionsLength; x++) {
            const optionIds2 = branchingOptions[x].optionIds;
            if (optionIds1.length && optionIds2.length) {
              const branchingOptionNames1 = _.map(optionIds1, "body");
              const branchingOptionNames2 = _.map(optionIds2, "body");
              const errorMsg = "Selected answer combinations must be unique.";
              if (!this.selectedQuestion.branchingOptions[x].error.includes(errorMsg) && JSON.stringify(branchingOptionNames1) === JSON.stringify(branchingOptionNames2)) {
                this.selectedQuestion.branchingOptions[x].error.push(errorMsg);
                this.$set(branchingOptions[x], "optionHasError", true);
              }
            }
          }
        }
      }
    },
    validateRangeOptions() {
      const branchingOptions = this.selectedQuestion.branchingOptions;
      const branchingOptionsLength = this.selectedQuestion.branchingOptions.length;
      for (let a = 0; a < branchingOptionsLength; a++) {
        const startValue = branchingOptions[a].startValue;
        const endValue = branchingOptions[a].endValue;

        //----------------------Validation to check partially empty vlaues-----------------
        if (startValue !== "" && endValue !== "" && this.BranchingHelper.checkIfNextQuestionHasValue(branchingOptions[a].nextQuestionId)) {
          //All values are filled
        } else if (startValue === "" && endValue === "" && this.BranchingHelper.checkIfNextQuestionIsEmpty(branchingOptions[a].nextQuestionId)) {
          //All values are empty
        } else {
          if (this.BranchingHelper.checkIfNextQuestionIsEmpty(branchingOptions[a].nextQuestionId)) {
            //Skip to step is not selected.
            this.$set(branchingOptions[a], "isSkipToStepEmpty", true);
          }
          if (startValue !== "" && endValue !== "") {
            this.$set(branchingOptions[a], "noEmptyRange", true);
          }

          this.selectedQuestion.branchingOptions[a].error.push("Please populate all required fields.");
        }

        //-------------------Validation to check decimal and there places when decimals allowed------------------
        if (startValue !== "" && this.selectedQuestion.allowDecimals && !this.BranchingHelper.hasProperDecimalPlaces(startValue, this.selectedQuestion.decimalValue)) {
          this.selectedQuestion.branchingOptions[a].error.push(`Up to ${this.selectedQuestion.decimalValue} decimals are allowed.`);
        } else if (endValue !== "" && this.selectedQuestion.allowDecimals && !this.BranchingHelper.hasProperDecimalPlaces(endValue, this.selectedQuestion.decimalValue)) {
          this.selectedQuestion.branchingOptions[a].error.push(`Up to ${this.selectedQuestion.decimalValue} decimals are allowed.`);
        }

        //-------------------Validation to check decimal when decimals not allowed------------------
        if (startValue !== "" && !this.selectedQuestion.allowDecimals && this.BranchingHelper.hasDecimal(startValue)) {
          this.selectedQuestion.branchingOptions[a].error.push(`Decimals not allowed.`);
        } else if (endValue != "" && !this.selectedQuestion.allowDecimals && this.BranchingHelper.hasDecimal(endValue)) {
          this.selectedQuestion.branchingOptions[a].error.push(`Decimals not allowed.`);
        }

        //-------------------Validation to check negative values when not allowed------------------
        if (startValue !== "" && !this.selectedQuestion.allowNegative && Number(startValue) < 0) {
          this.selectedQuestion.branchingOptions[a].error.push(`Negative value not allowed.`);
        } else if (endValue !== "" && !this.selectedQuestion.allowNegative && Number(endValue) < 0) {
          this.selectedQuestion.branchingOptions[a].error.push(`Negative value not allowed.`);
        }

        //-------------------Validation to check character limit when configured------------------
        if (startValue !== "" && this.selectedQuestion.characterLimited && !this.BranchingHelper.hasNumberUnderLimit(startValue, this.selectedQuestion.textLimit)) {
          this.selectedQuestion.branchingOptions[a].error.push(`Number limit can not be more than ${this.selectedQuestion.textLimit}.`);
        } else if (endValue !== "" && this.selectedQuestion.characterLimited && !this.BranchingHelper.hasNumberUnderLimit(endValue, this.selectedQuestion.textLimit)) {
          this.selectedQuestion.branchingOptions[a].error.push(`Number limit can not be more than ${this.selectedQuestion.textLimit}.`);
        }

        //---------------------Validations to check if the provided range is in ascending order--------
        if (startValue !== "" && endValue !== "" && Number(startValue) > Number(endValue)) {
          this.selectedQuestion.branchingOptions[a].error.push("Ending range must be greater than or equal to the starting range");
        }

        //---------------------Validations to check if the provided values are under min/max range--------
        if (this.selectedQuestion.hasMinMax) {
          if (startValue !== "" && endValue !== "") {
            if (!this.BranchingHelper.isUnderRange(startValue, endValue, this.selectedQuestion.minValue, this.selectedQuestion.maxValue)) {
              this.selectedQuestion.branchingOptions[a].error.push(`Please enter a number that is between ${this.selectedQuestion.minValue} (Min) and ${this.selectedQuestion.maxValue} (Max).`);
            } else if (!this.BranchingHelper.isUnderMin(startValue, this.selectedQuestion.minValue)) {
              this.selectedQuestion.branchingOptions[a].error.push(`Please enter a number that is greater than or equal to ${this.selectedQuestion.minValue} (Min).`);
            } else if (!this.BranchingHelper.isUnderMax(endValue, this.selectedQuestion.maxValue)) {
              this.selectedQuestion.branchingOptions[a].error.push(`Please enter a number that is less than or equal to ${this.selectedQuestion.maxValue} (Max).`);
            }
          }
        }
      }

      //------------Validations for Overlapping values-------------------------
      let i, x;
      for (i = 0; i < branchingOptionsLength - 1; i++) {
        const startValue1 = branchingOptions[i].startValue;
        const endValue1 = branchingOptions[i].endValue;
        for (x = i + 1; x < branchingOptionsLength; x++) {
          const startValue2 = branchingOptions[x].startValue;
          const endValue2 = branchingOptions[x].endValue;
          if (startValue1 !== "" && endValue1 !== "" && startValue2 !== "" && endValue2 !== "") {
            const errorMsg = "Response ranges cannot overlap with other branches.";
            if (!this.selectedQuestion.branchingOptions[x].error.includes(errorMsg) && this.BranchingHelper.checkOverlap(branchingOptions[i], branchingOptions[x])) {
              this.selectedQuestion.branchingOptions[x].error.push(errorMsg);
            }
          }
        }
      }
    },
    hasAnyLoopThroughStepSelected() {
      for (let a = 0; a < this.selectedQuestion.options.length; a++) {
        if (this.selectedQuestion.options[a].nextQuestionIdList.length) {
          return true;
        }
      }
      return false;
    },
    resetErrors() {
      delete this.selectedQuestion.error;
      delete this.selectedQuestion.recursiveLoopEndQuestionEmpty;
      if (this.selectedQuestion.branchingOptions) {
        this.selectedQuestion.branchingOptions.forEach(e => {
          e.error = [];
          delete e.isSkipToStepEmpty;
          delete e.noEmptyRange;
          delete e.optionHasError;
        });
      }
    },
    deleteEmptyBranchingRow() {
      const bo = this.selectedQuestion.branchingOptions;
      const deleteIndexes = [];
      if (!bo || !bo.length) {
        return;
      }

      if (this.selectedQuestion.type == "MultiChoice" && this.selectedQuestion.multipleSelection == true) {
        for (let a = 0; a < bo.length; a++) {
          if (bo[a].optionIds.length == 0 && this.BranchingHelper.checkIfNextQuestionIsEmpty(bo[a].nextQuestionId)) {
            deleteIndexes.push(a);
          }
        }
      } else {
        for (let a = 0; a < bo.length; a++) {
          if (bo[a].startValue == "" && bo[a].endValue == "" && this.BranchingHelper.checkIfNextQuestionIsEmpty(bo[a].nextQuestionId)) {
            deleteIndexes.push(a);
          }
        }
      }

      for (let i = deleteIndexes.length - 1; i >= 0; i--) {
        this.selectedQuestion.branchingOptions.splice(deleteIndexes[i], 1);
      }
    },
    branchingHasAnyErrors() {
      let hasError = false;
      if (this.selectedQuestion.error && this.selectedQuestion.error.length) {
        //MC MS REC
        hasError = true;
      } else if (this.selectedQuestion.branchingOptions) {
        hasError = _.find(this.selectedQuestion.branchingOptions, function(o) {
          return o.error.length > 0;
        });
      } else if (this.selectedQuestion.options) {
        hasError = _.find(this.selectedQuestion.options, function(o) {
          return o.error.length > 0;
        });
      }

      if (hasError) {
        return true;
      } else {
        return false;
      }
    },
    toggleChips(branchingOptions) {
      const optionsLength = branchingOptions.optionIds.length;
      branchingOptions.chipsLimitBy = branchingOptions.chipsLimitBy === this.default_chips_limit ? optionsLength : this.default_chips_limit;
    },
    loopEndQuestions() {
      let loopEndQuestions = [];
      for (let i = 0; i < this.selectedQuestion.options.length; i++) {
        const nextQuestionIdList = this.selectedQuestion.options[i].nextQuestionIdList;
        loopEndQuestions = _.concat(loopEndQuestions, nextQuestionIdList);
      }

      this.recursiveLoopEndQuestions = _.filter(this.activityQuestions, function(o) {
        return !loopEndQuestions.includes(o.id);
      });
    }
  }
};
</script>

<style lang="scss">
@media (min-width: 992px) {
  #branching-setup-modal {
    &.general {
      .modal-dialog {
        min-width: 561px !important;
      }
    }

    &.non-general {
      .modal-dialog {
        min-width: 762px !important;
      }
    }

    .modal-dialog {
      vertical-align: middle;
    }
  }
}

#branching-setup-modal {
  .modal-content {
    .modal-header {
      border-bottom: 0;
      color: #4a4a4a;
      padding: 2rem;
      padding-bottom: 0;
      font-family: Roboto-Bold;

      h5 {
        font-size: 0.875rem;
      }
    }

    .modal-body {
      padding: 2rem;
      padding-top: 5px;
      font-size: 14px;
      color: #4a4a4a;

      .question-title {
        font-size: 18px;
        margin-bottom: 22px;
      }

      .form-check-inline {
        .form-check-label {
          font-weight: normal !important;
          margin-bottom: 0;
          cursor: pointer;
        }
      }

      .general-branching {
        border-top: solid 1px #d3dfe4;
        padding: 12px 0;
      }

      .trash-can {
        cursor: pointer;

        &.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      .normal-branching {
        &.disabled {
          opacity: 0.4;
          pointer-events: none;
        }
      }

      .form-check {
        &.form-check-input {
          cursor: pointer;
        }

        &.form-check-inline {
          font-size: 12px;
          color: #2d3652;
        }
      }

      .error-text {
        color: #f16559;
        font-family: Roboto-Bold;
        font-size: 12px;
        width: 100%;
      }

      .advance-branching {
        margin-top: 24px;

        &.disabled {
          opacity: 0.4;
          pointer-events: none;
        }

        .header {
          font-size: 12px;
          font-family: Roboto-Bold;
          color: #979797;
          border-bottom: solid 1px #d3dfe4;
          padding-bottom: 4px;
        }

        input {
          &.form-control {
            border-radius: 4px;
            border: solid 1px #d3dfe4;
          }
        }

        /*.model-fixed-branching{
                    max-height: calc(100vh - 25rem);
                    overflow: auto;
                    padding-right: 4px;
                }*/
        .branching-opt-row {
          border-bottom: solid 1px #d3dfe4;
          padding: 12px 0;

          &:last-of-type {
            border-bottom: 0;
          }

          /*Remove spinner from input type number*/
          /* Chrome, Safari, Edge, Opera */
          input::-webkit-outer-spin-button,
          input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          /* Firefox */
          input[type="number"] {
            -moz-appearance: textfield;
            appearance: textfield;
          }

          .min-max {
            color: #666669;
            font-family: Roboto-Regular;
            font-size: 12px;
            padding-top: 11px;
            width: 100%;
          }

          &.has-error {
            input[type="number"] {
              &.form-control {
                border: solid 1px #f16559;
              }
            }

            .no-empty-error {
              input[type="number"] {
                &.form-control {
                  border: solid 1px #d3dfe4;
                }
              }
            }
          }

          .selected-options-chips {
            font-size: 12px;
            width: 55%;
            vertical-align: middle;

            .see-more-less {
              color: #4c8ce4;
              font-size: 14px;
              font-family: Roboto-Bold;
              display: inline-block;
            }

            .chip {
              padding: 5px 12px;
              border-radius: 4px;
              color: #4a4a4a;
              background-color: #e0e0e0;
              margin-right: 10px;
              margin-bottom: 4px;
              display: inline-block;

              .fa {
                color: #666669;
                vertical-align: middle;
                cursor: pointer;
                font-size: 15px;
                margin-left: 5px;
              }

              &:last-of-type {
                margin-right: 0;
              }
            }
          }
        }
      }

      .recursive-branching-toggle {
        color: #2d3652;

        .fa {
          color: #959fa4;
          font-size: 15px;
          margin-left: 5px;
        }

        /* toggle btn css - START */
        .switch-toggle-btn {
          position: relative;
          display: inline-block;
          width: 56px;
          height: 24px;
          font-size: 14px;
          top: -2px;
          left: 10px;
          margin-left: 5px;

          &:hover {
            cursor: pointer;
          }
        }

        .switch-toggle-btn input {
          opacity: 0;
        }

        .switch-toggle-btn .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #b0bac9;
          -webkit-transition: 0.4s;
          transition: 0.4s;
          border-radius: 16px;
        }

        .switch-toggle-btn .slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 4px;
          top: 3px;
          background-color: #fff;
          -webkit-transition: 0.4s;
          transition: 0.4s;
          border-radius: 50%;
        }

        .switch-toggle-btn input:checked + .slider {
          background-color: #2ecd6c;
        }

        .switch-toggle-btn input:focus + .slider {
          box-shadow: 0 0 1px #2ecd6c;
        }

        .switch-toggle-btn input:checked + .slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: -41px;
          bottom: -1px;
          top: 3px;
          background-color: #fff;
          -webkit-transition: 0.4s;
          transition: 0.4s;
          border-radius: 50%;
          -webkit-transform: translateX(75px);
          -ms-transform: translateX(75px);
          transform: translateX(75px);
        }

        .switch-toggle-btn .text {
          color: white;
        }

        .switch-toggle-btn .text:after {
          position: absolute;
          top: 2px;
          right: 10px;
          content: "Off";
          color: #fff;
          -webkit-transition: opacity 0.5s ease-in-out;
          -moz-transition: opacity 0.5s ease-in-out;
          transition: opacity 0.5s ease-in-out;
        }

        .switch-toggle-btn .text:before {
          position: absolute;
          top: 1px;
          left: 10px;
          content: "On";
          color: #fff;
          -webkit-transition: opacity 0.5s ease-in-out;
          -moz-transition: opacity 0.5s ease-in-out;
          transition: opacity 0.5s ease-in-out;
        }

        .switch-toggle-btn input + .slider + .text:after {
          opacity: 1;
        }

        .switch-toggle-btn input + .slider + .text:before {
          opacity: 0;
        }

        .switch-toggle-btn input:checked + .slider + .text:after {
          opacity: 0;
        }

        .switch-toggle-btn input:checked + .slider + .text:before {
          opacity: 1;
        }

        /* toggle btn css - END */
      }

      .footer {
        margin-top: 34px;

        .col-8 {
          flex: 0 0 63.666667%;
          max-width: 63.666667%;
        }

        .col-4 {
          flex: 0 0 36.333333%;
          max-width: 36.333333%;
        }

        //overflow: hidden;
        .adjust-col-width {
          .col-8 {
            max-width: 70%;
            flex: 0 0 70%;
          }

          .col-4 {
            max-width: 30%;
            flex: 0 0 30%;
          }
        }

        button {
          font-size: 14px;

          &.btn-add-another {
            color: #4c8ce4;
            font-family: Roboto-Bold;
            background: none;
            border: 0;
            padding-left: 0;

            &.disabled {
              opacity: 0.4;
              pointer-events: none;
            }

            &:focus {
              box-shadow: none;
            }

            &:active {
              background: none;
              border: none;
            }
          }

          &.btn-primary {
            background-color: #4c8ce4;
            border-color: #4c8ce4;
            border-radius: 4px;

            padding: 8px 28px;

            &:hover {
              background-color: #34537d;
              border-color: #34537d;
            }

            &:disabled {
              opacity: 0.4;
            }

            &:focus {
              box-shadow: none;
            }
          }

          &.btn-cancel {
            padding: 8px 24px;
            color: #666669;
            background: none;
            border: 0;

            &:hover {
              color: #4a4a4a;
              background: none;
              border: none;
              padding: 8px 24px;
              outline: none;
              box-shadow: none;
            }

            &:active {
              background: none;
              border: none;
            }

            &:focus {
              box-shadow: none;
            }
          }
        }
      }
    }
  }
}
</style>
